import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { spotlightRefCountAtom } from '@frontend/atoms';
import { type TSpotlightAPI, useSpotlightAPI } from '@frontend/components/Spotlight/store';

/**
 * Opt a component into the global Spotlight and get the imperative API.
 *
 * While `enabled`, this ref-counts the Spotlight up on mount and down on unmount
 * — the host only renders while the count is `> 0`. Callers own any gating
 * (e.g. a feature flag): pass `enabled` accordingly. Registration of commands is
 * independent of this — register them regardless and gate only the trigger.
 *
 * Returns the shared `TSpotlightAPI` (open/close/toggle + register/mode-stack
 * methods). The API always acts on the global state; `enabled` only controls
 * whether this caller contributes to the ref-count.
 */
export function useSpotlight(enabled = false): TSpotlightAPI {
  const setRefCount = useSetAtom(spotlightRefCountAtom);
  const api = useSpotlightAPI();

  useEffect(() => {
    if (!enabled) return;
    setRefCount(c => c + 1);
    return () => setRefCount(c => c - 1);
  }, [enabled, setRefCount]);

  return api;
}
